import type { UseQueryResult } from '@tanstack/react-query';
import { TokenUtil } from 'services/TokenUtil';
import { ASSIST_API } from 'services/assist-api/axios';
import { getCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import type { BaseOrganisation } from 'services/assist-api/entities/customers/customers';
import { getOrganisation } from 'services/assist-api/entities/customers/customers';
import type { ROLE } from 'services/assist-api/entities/role/roles';
import { useQueryAssist } from 'services/assist-api/query-client';

export type MeCustomerUser = Omit<BaseCustomerUser, 'user'>;

export type Me = API_JSON & {
  firstname: string;
  lastname: string;
  email: string;
  username: string;
  status: string;
  systemRoles?: ROLE[];
  customerRoles?: ROLE[];
  gender?: API_JSON;
  ethnicities?: API_JSON[];
  customer?: BaseOrganisation;
  customerUser?: MeCustomerUser;
};

const groups = ['role-name'];
const include = 'customers,ethnicities,roles';

export const getMe = async (): Promise<Me> => {
  const customer = TokenUtil.getAccessTokenCustomer();
  const meJson = (await ASSIST_API.get('me', { params: { groups, include } })).data?.json;
  const requestCustomer = customer?.id && getOrganisation(customer.id);
  const requestCustomerUser =
    customer?.id &&
    getCustomerUsers({
      filters: { customer: customer.id, user: meJson.id }
    });

  const fullCustomer = (await requestCustomer)?.json;
  const customerUserJson = ((await requestCustomerUser)?.json || [])[0];

  return {
    ...meJson,
    customer: fullCustomer,
    customerUser: customerUserJson,
    systemRoles: meJson?.roles?.map(({ name }) => name),
    customerRoles:
      (customerUserJson?.confirmed &&
        customerUserJson?.status === 'active' &&
        customerUserJson?.roles?.map(({ name }) => name)) ||
      []
  };
};

export const useMe = (options?): UseQueryResult<Me, unknown> =>
  useQueryAssist<Me>(['me'], () => getMe(), {
    ...options,
    allowNoFilters: true,
    staleTime: STALE_SHORT,
    retry: 0
  });
